import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { analytics } from '../services/analytics';
import PlaylistForm from '../components/PlaylistForm';
import LoadingSpinner from '../components/LoadingSpinner';

function EditPlaylist() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    analytics.pageView('edit_playlist');
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && id) {
      loadPlaylist();
    }
  }, [auth.isAuthenticated, id]);

  const loadPlaylist = async () => {
    try {
      setIsLoading(true);
      const data = await api.getPlaylist(id);
      setPlaylist(data.playlist);
    } catch (err) {
      setError(err.message);
      analytics.error('load_playlist_failed', { message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);

      // Update playlist
      await api.updatePlaylist(id, {
        name: data.name,
        url: data.url
      });

      // Handle PIN changes separately
      if (data.pin !== undefined) {
        if (data.pin === null && data.currentPin) {
          await api.removePin(id, data.currentPin);
          analytics.pinRemoved(id);
        } else if (data.pin && data.currentPin) {
          await api.setPin(id, data.pin, data.currentPin);
          analytics.pinChanged(id);
        } else if (data.pin && !playlist.hasPin) {
          await api.setPin(id, data.pin);
          analytics.pinSet(id);
        }
      }

      analytics.playlistUpdated(id);
      navigate('/');
    } catch (err) {
      setError(err.message);
      analytics.error('update_playlist_failed', { message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  if (!auth.isAuthenticated) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading playlist..." />;
  }

  if (!playlist) {
    return (
      <div className="page-container">
        <div className="error-message">
          Playlist not found
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 style={{ marginBottom: '24px' }}>Edit Playlist</h1>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <PlaylistForm
        initialData={playlist}
        onSubmit={handleSubmit}
        isLoading={isSaving}
        submitLabel="Save Changes"
      />
    </div>
  );
}

export default EditPlaylist;
